import { useState, useEffect } from 'react';
import { doc, collection, onSnapshot, query, where, orderBy, getDoc, getDocs, limit, startAfter } from "firebase/firestore";
import { db } from "../../firebase";
import { toast, ToastContainer } from 'react-toastify';
import { closeCashRegister, deleteCashClose, editCashClose, voidCashReturn } from '../../lib/cashApi';
import { calculateTurnSalesAccounting } from '../../lib/cashSalesAccounting';


// Formato moneda COP sin decimales
const fmtCOP = (n) =>
  (Number(n) || 0).toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  });

const CLOSE_CASH_TOAST_ID = 'cash-close-toasts';
const AUTHORIZED_CLOSE_DELETE_ADMIN_EMAIL = 'andresplata35@gmail.com';


// Sustituye groupMethod por normalizeMethod:
const normalizeMethod = (m) => {
  const k = (m || '').toString().trim().toLowerCase();
  if (!k) return 'Otros';
  if (k.includes('efect')) return 'Efectivo';

  // Tarjetas
  if (
    k.includes('tarjeta') ||
    k.includes('debito') || k.includes('débito') ||
    k.includes('credito') || k.includes('crédito') ||
    k.includes('visa') || k.includes('mastercard')
  ) return 'Tarjeta';

  // Bancos / billeteras (cada una por su nombre)
  if (k.includes('nequi')) return 'Nequi';
  if (k.includes('daviplata')) return 'Daviplata';
  if (k.includes('bancolombia')) return 'Bancolombia';
  if (k.includes('bbva')) return 'BBVA';
  if (k.includes('davivienda')) return 'Davivienda';
  if (k.includes('rappi')) return 'Rappi';

  // Otros canales frecuentes
  if (k.includes('pse')) return 'PSE';
  if (k.includes('qr')) return 'QR';

  return 'Otros';
};


// Convierte Timestamps de Firestore o strings varios a Date válida
const parseDateLoose = (val) => {
  if (!val) return null;
  // Firestore Timestamp
  if (typeof val?.toDate === "function") return val.toDate();

  if (typeof val === "string") {
    const s = val.trim();

    // dd/mm/aaaa
    const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m1) return new Date(`${m1[3]}-${m1[2]}-${m1[1]}T00:00:00`);

    // yyyy-mm-dd (lo hacemos ISO explícito)
    const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m2) return new Date(`${s}T00:00:00`);

    // ISO o algo que el motor pueda leer
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }

  const d = new Date(val);
  return isNaN(d) ? null : d;
};

// Saca la fecha/hora de inicio del turno usando los campos disponibles
const getTurnStart = (cash) => {
  // 1) Si tienes openedAt (Timestamp) úsalo:
  if (cash?.openedAt && typeof cash.openedAt.toDate === "function") {
    return cash.openedAt.toDate();
  }

  // 2) Construye desde date + (time|hora)
const base = parseDateLoose(cash?.date || cash?.fecha) || new Date();
const t = cash?.time || cash?.hora;

  if (t && typeof t === "string") {
    // soporta "HH:mm" o "HH:mm:ss"
    const mh = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (mh) {
      base.setHours(parseInt(mh[1], 10), parseInt(mh[2], 10), parseInt(mh[3] || "0", 10), 0);
      return base;
    }
    // si viene "7:00 p. m." lo ignoramos y dejamos 00:00 para no romper
  }
  return base;
};



const CashRegisterCloseView = ({ onBack, currentUser, currentCashRegister, products }) => {
  const currentRole = String(currentUser?.role || '').trim().toLowerCase();
  const currentEmail = String(currentUser?.email || '').trim().toLowerCase();
  const canEditClosings = currentRole === 'ti' || currentRole === 'administrador' || currentRole === 'admin';
  const canDeleteClosings = currentRole === 'ti'
    || (currentRole === 'administrador' && currentEmail === AUTHORIZED_CLOSE_DELETE_ADMIN_EMAIL);
  const [currentSubView, setCurrentSubView] = useState('close');
  const [reportFormat, setReportFormat] = useState('thermal');
// ⬇️ NUEVO: turno abierto local cuando no viene por props
  const [openTurnLocal, setOpenTurnLocal] = useState(null);


  const [loadingOpenTurn, setLoadingOpenTurn] = useState(true);



  useEffect(() => {
    const userEmail = currentUser?.email ?? currentUser ?? null;
    if (!userEmail) { setOpenTurnLocal(null); setLoadingOpenTurn(false); return; }

    // Si ya viene por props, no dublesuscribas
    if (currentCashRegister?.estado === 'abierta') {
      setOpenTurnLocal(null);
      setLoadingOpenTurn(false);
      return;
    }



    const q = query(
      collection(db, "cajas"),
      where("user", "==", userEmail),
      where("estado", "==", "abierta"),
      orderBy("openedAt", "desc"),
      limit(1)
    );

    const unsub = onSnapshot(
      q,
      (ss) => {
        console.log('[FS][onSnapshot] CashRegisterCloseView.openTurnLocal', ss.size);
        setLoadingOpenTurn(false);
        if (!ss.empty) {
          const d = ss.docs[0];
          setOpenTurnLocal({ id: d.id, ...d.data() });
        } else {
          setOpenTurnLocal(null);
        }
      },
      (err) => {
        console.error("Error cargando turno abierto:", err);
        setLoadingOpenTurn(false);
        setOpenTurnLocal(null);
      }
    );



    return () => unsub();
  }, [currentUser, currentCashRegister]);

 const effectiveCash =
    (currentCashRegister?.estado === 'abierta' ? currentCashRegister : null) ||
    openTurnLocal ||
    null;

const [closingAmountInput, setClosingAmountInput] = useState('');
const closingAmount = closingAmountInput === '' ? 0 : Number(closingAmountInput);
const [observations, setObservations] = useState('');

  // 🟩 estados base que alimentan los cálculos
  const [salesData, setSalesData] = useState([]);
  const [incomingData, setIncomingData] = useState([]);
  const [outgoingData, setOutgoingData] = useState([]);
  const [salesCursor, setSalesCursor] = useState(null);
  const [incomingCursor, setIncomingCursor] = useState(null);
  const [outgoingCursor, setOutgoingCursor] = useState(null);
  const [hasMoreSales, setHasMoreSales] = useState(false);
  const [hasMoreIncoming, setHasMoreIncoming] = useState(false);
  const [hasMoreOutgoing, setHasMoreOutgoing] = useState(false);
  const [expensesInTurn, setExpensesInTurn] = useState([]);
  const [advanceTransfers, setAdvanceTransfers] = useState([]);
  const [returnTransfers, setReturnTransfers] = useState([]);

  // 🟩 estados derivados/visuales del turno
  const [salesInTurn, setSalesInTurn] = useState([]);
  const [manualIncomingInTurn, setManualIncomingInTurn] = useState([]);
  const [manualOutgoingInTurn, setManualOutgoingInTurn] = useState([]);
  const [userClosingHistory, setUserClosingHistory] = useState([]);

  // Modal de detalle de cierre
  
const [detailOpen, setDetailOpen] = useState(false);
const [detailData, setDetailData] = useState(null);
const [detailLoading, setDetailLoading] = useState(false);

useEffect(() => {
  if (detailOpen) {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }
}, [detailOpen]);

// Modal de edición de cierre
const [editOpen, setEditOpen] = useState(false);
const [editLoading, setEditLoading] = useState(false);
const [editData, setEditData] = useState(null); // doc completo del cierre
const [editFinalAmount, setEditFinalAmount] = useState('');
const [editObservations, setEditObservations] = useState('');
const [deletingCloseId, setDeletingCloseId] = useState(null);
const [voidingReturnId, setVoidingReturnId] = useState(null);




// === Modal de confirmación de cierre ===
const [confirmOpen, setConfirmOpen] = useState(null); 
// null | "empty" | "normal"

const abrirConfirmacionCierre = () => {
  if (closingAmountInput === '') {
   toast.info("Ingresa el monto contado en caja antes de cerrar.", { icon: "💡", containerId: CLOSE_CASH_TOAST_ID });
   return;
 }
  if (totalNumberOfSales === 0) {
    setConfirmOpen("empty"); // si no hubo ventas
  } else {
    setConfirmOpen("normal");
  }
};

const cerrarConfirmacion = () => setConfirmOpen(null);

// lo que antes pasaba dentro del confirm(true)
const confirmarCierreTurno = async () => {
  setConfirmOpen(null);

 // ⬇️ Guard: si no hay turno, no intentes cerrar
  if (!effectiveCash?.id) {
toast.info("No hay un turno abierto para cerrar.", { icon: "ℹ️", containerId: CLOSE_CASH_TOAST_ID });
    return;
  }

  try {
    const now = new Date();
    const closingTime = now.toLocaleTimeString('es-CO'); // visible al usuario

    const closedTurnData = {
      closingTime,
      estado: "cerrada",
      closedBy: currentUser?.email ?? currentUser ?? '',
      status: difference === 0 ? 'Cerrado (OK)' : (difference > 0 ? 'Cerrado (Sobrante)' : 'Cerrado (Faltante)'),
      finalAmount: closingAmount,
      expectedTotalInCash,
      difference,
      advanceTotal,
      returnTotal,
      adjustedBaseAmount,

      // KPIs que ya calculas arriba
      totalCashSales,
      totalCardSales,
      totalTransferSales,
      totalOtherSales,
      totalManualIncoming,
      totalManualOutgoing,
      totalSalesCancelledOrReturned,
      totalNumberOfSales,
      totalProductsSold,
      totalExpensesInTurn,
      expensesInTurn: (expensesInTurn || []).map(g => ({
        id: g.id,
        category: g.category || 'Gasto',
        amount: Number(g.amount || 0),
        date: g.date || g.dateStr || null,
        turnId: g.turnId || null,
        registeredBy: g.registeredBy || null,
      })),
      observations,

      paymentBreakdown: {
        ventas: salesByMethod,
        cobros: collectionsByMethod,
      },
      advanceTransfersSnapshot: advanceTransfers.map((t) => ({
        id: t.id,
        amount: Number(t.amount || 0),
        relatedUser: t.relatedUser || t.toTurnId || 'Sin usuario',
        description: t.description || t.note || '',
      })),
      returnTransfersSnapshot: returnTransfers.map((t) => ({
        id: t.id,
        amount: Number(t.amount || 0),
        relatedUser: t.relatedUser || t.fromTurnId || 'Sin usuario',
        description: t.description || t.note || '',
      })),
      efectivoRecibido,
      cambioEntregado,
      cashFromSalesNet,
    };

    await closeCashRegister(effectiveCash.id, closedTurnData);
 toast.success('Turno de caja cerrado con éxito', { icon: '✅', containerId: CLOSE_CASH_TOAST_ID });
    onBack();
  } catch (e) {
    console.error(e);
 toast.error(e?.message || "Ocurrió un error al cerrar la caja. Inténtalo de nuevo.", { icon: "⚠️", containerId: CLOSE_CASH_TOAST_ID });
  }
};

  // 1) Lecturas acotadas de ventas/ingresos/salidas (evita streams masivos)
  const MOVEMENTS_PAGE = 50; // máx 100, aquí 50 para ser conservador

  const buildBaseConstraints = () => {
    const constraints = [];
    if (effectiveCash?.id) {
      constraints.push(where('turnId', '==', effectiveCash.id));
    } else {
      // TODO: migrar todos los docs de ventas/ingresos/salidas para que siempre tengan turnId y evitar rangos abiertos.
      const end = new Date();
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000); // fallback ultra acotado: últimas 24h
      constraints.push(where('date', '>=', start.toISOString()));
      constraints.push(where('date', '<=', end.toISOString()));
    }

    return { constraints };
  };

  const fetchCollectionPage = async ({ colName, cursor, append }) => {
    const { constraints } = buildBaseConstraints();
    let qy = query(collection(db, colName), ...constraints, orderBy('date', 'desc'), limit(MOVEMENTS_PAGE));
    if (cursor) qy = query(qy, startAfter(cursor));
    const snap = await getDocs(qy);
    console.log('[FS][getDocs] CashRegisterCloseView.movements', colName, snap.size);
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const last = snap.docs[snap.docs.length - 1] || null;
    return { docs, last, hasMore: Boolean(last) && snap.docs.length === MOVEMENTS_PAGE };
  };

  // El cierre debe contabilizar el turno completo, no solo la primera página.
  const fetchAllCollectionPages = async (colName) => {
    const docs = [];
    let cursor = null;
    let hasMore = true;
    while (hasMore) {
      const page = await fetchCollectionPage({ colName, cursor, append: docs.length > 0 });
      docs.push(...page.docs);
      cursor = page.last;
      hasMore = page.hasMore;
    }
    return { docs, last: cursor, hasMore: false };
  };

  useEffect(() => {
    let cancelled = false;
    const loadInitial = async () => {
      try {
        const [salesPage, inPage, outPage] = await Promise.all([
          fetchAllCollectionPages('ventas'),
          fetchAllCollectionPages('ingresos'),
          fetchAllCollectionPages('salidas'),
        ]);
        if (cancelled) return;
        setSalesData(salesPage.docs);
        setSalesCursor(salesPage.last);
        setHasMoreSales(salesPage.hasMore);

        setIncomingData(inPage.docs);
        setIncomingCursor(inPage.last);
        setHasMoreIncoming(inPage.hasMore);

        setOutgoingData(outPage.docs);
        setOutgoingCursor(outPage.last);
        setHasMoreOutgoing(outPage.hasMore);
      } catch (e) {
        console.error('Error cargando datos de cierre', e);
      }
    };
    loadInitial();
    return () => { cancelled = true; };
  }, [effectiveCash]);

  const loadMoreMovements = async () => {
    try {
      if (hasMoreSales) {
        const page = await fetchCollectionPage({ colName: 'ventas', cursor: salesCursor, append: true });
        setSalesData(prev => [...prev, ...page.docs]);
        setSalesCursor(page.last);
        setHasMoreSales(page.hasMore);
      }
      if (hasMoreIncoming) {
        const page = await fetchCollectionPage({ colName: 'ingresos', cursor: incomingCursor, append: true });
        setIncomingData(prev => [...prev, ...page.docs]);
        setIncomingCursor(page.last);
        setHasMoreIncoming(page.hasMore);
      }
      if (hasMoreOutgoing) {
        const page = await fetchCollectionPage({ colName: 'salidas', cursor: outgoingCursor, append: true });
        setOutgoingData(prev => [...prev, ...page.docs]);
        setOutgoingCursor(page.last);
        setHasMoreOutgoing(page.hasMore);
      }
    } catch (e) {
      console.error('Error cargando m???s movimientos', e);
    }
  };

// 2) Gastos del turno (solo por turnId)
useEffect(() => {
  let cancelled = false;
  const load = async () => {
    if (!effectiveCash?.id) {
      setExpensesInTurn([]);
      return;
    }
    try {
      const qPorTurno = query(
        collection(db, "gastos"),
        where("turnId", "==", effectiveCash.id),
        where("affectsCash", "==", true),
        limit(300)
      );
      const snap = await getDocs(qPorTurno);
      console.log('[FS][getDocs] CashRegisterCloseView.gastos', snap.size);
      if (!cancelled) {
        setExpensesInTurn(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error(err);
      if (!cancelled) setExpensesInTurn([]);
    }
  };
  load();
  return () => { cancelled = true; };
}, [effectiveCash?.id]);

useEffect(() => {
  let cancelled = false;
  const load = async () => {
    if (!effectiveCash?.id) {
      setAdvanceTransfers([]);
      setReturnTransfers([]);
      return;
    }
    try {
      const qAdvance = query(
        collection(db, "internalTransfers"),
        where("fromTurnId", "==", effectiveCash.id),
        where("type", "==", "advance"),
        limit(200)
      );
      const qReturn = query(
        collection(db, "internalTransfers"),
        where("toTurnId", "==", effectiveCash.id),
        where("type", "==", "return"),
        limit(200)
      );
      const [snapAdv, snapRet] = await Promise.all([getDocs(qAdvance), getDocs(qReturn)]);
      console.log('[FS][getDocs] CashRegisterCloseView.internalTransfers', snapAdv.size + snapRet.size);
      if (cancelled) return;
      setAdvanceTransfers(
        snapAdv.docs.map(d => ({ id: d.id, ...d.data() })).filter(entry => entry.affectsCash !== false)
      );
      setReturnTransfers(
        snapRet.docs.map(d => ({ id: d.id, ...d.data() })).filter(entry => entry.affectsCash !== false)
      );
    } catch (err) {
      console.error(err);
      if (!cancelled) {
        setAdvanceTransfers([]);
        setReturnTransfers([]);
      }
    }
  };
  load();
  return () => { cancelled = true; };
}, [effectiveCash?.id]);

// === Filtros Historial (NUEVO) ===
const [histDateFrom, setHistDateFrom] = useState('');
const [histDateTo, setHistDateTo]     = useState('');
const [histUser, setHistUser]         = useState('');
const [userOptions, setUserOptions]   = useState([]);

const startOfDay = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);     // local
};
const endOfDay = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999); // local
};

const isStaff = currentUser?.role &&
  ['admin','Admin','ADMIN','ti','TI','Administrador','ADMINISTRADOR'].includes(currentUser.role);


  // 3) Filtrado de datos AL INTERIOR DEL TURNO
useEffect(() => {
 if (!effectiveCash) return;

const turnStartDate = getTurnStart(effectiveCash);
  const turnStartTime = turnStartDate.getTime();
  const nowTs = Date.now();

const sales = salesData.filter(sale => sale.turnId === effectiveCash.id);  setSalesInTurn(sales);

const incoming = incomingData.filter(entry =>
  entry.entryType !== 'compra' &&
  entry.affectsCash !== false &&
   entry.turnId === effectiveCash.id
);
  setManualIncomingInTurn(incoming);

  // Salidas (si las quieres para KPI visual; no entran al esperado)
 const outgoing = outgoingData.filter(exit =>
   !exit.saleId &&
   !(String(exit.reason || '').toLowerCase().includes('venta')) &&
   exit.turnId === effectiveCash.id
 );
  setManualOutgoingInTurn(outgoing);
}, [effectiveCash, salesData, incomingData, outgoingData]);

// === HISTORIAL DE CIERRES con paginaci?n (consulta puntual, sin listener) ===
const PAGE_SIZE = 20;
const [page, setPage] = useState(0);
const [pageCursors, setPageCursors] = useState([]);
const [hasNext, setHasNext] = useState(false);
const [loadingHist, setLoadingHist] = useState(false);
const HIST_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutos

const histCacheKey = ({ isStaffFlag, userEmail, histUserVal, from, to }) =>
  `cashCloseHist:v3:${isStaffFlag ? 'staff' : 'user'}:${userEmail || 'anon'}:${histUserVal || 'all'}:${from || 'none'}:${to || 'none'}`;

const readHistCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.ts || !Array.isArray(data.rows)) return null;
    if (Date.now() - data.ts > HIST_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const writeHistCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), ...payload }));
  } catch {
    // ignorar errores de cuota
  }
};

useEffect(() => {
  const userEmail = currentUser?.email ?? currentUser ?? null;
  if (!userEmail || currentSubView !== 'history') return;

  let cancelled = false;
  const loadHist = async () => {
    setLoadingHist(true);

    const cacheKey = histCacheKey({
      isStaffFlag: !!isStaff,
      userEmail,
      histUserVal: histUser,
      from: histDateFrom,
      to: histDateTo,
    });

    const cajasRef = collection(db, "cajas");
    const wheres = [where("estado", "==", "cerrada")];

    if (!isStaff) {
      wheres.push(where("user", "==", userEmail));
    } else if (histUser) {
      wheres.push(where("user", "==", histUser));
    }

    const from = startOfDay(histDateFrom);
    const to = endOfDay(histDateTo);
    if (from) wheres.push(where("closedAt", ">=", from));
    if (to) wheres.push(where("closedAt", "<=", to));

    if (page === 0) {
      const cached = readHistCache(cacheKey);
      if (cached) {
        setUserClosingHistory(cached.rows || []);
        setHasNext(!!cached.hasNext);
        setLoadingHist(false);
        return;
      }
    }

    let qHist = query(
      cajasRef,
      ...wheres,
      orderBy("closedAt", "desc"),
      limit(PAGE_SIZE + 1)
    );

    const cursor = pageCursors[page - 1];
    if (cursor) {
      qHist = query(
        cajasRef,
        ...wheres,
        orderBy("closedAt", "desc"),
        startAfter(cursor),
        limit(PAGE_SIZE + 1)
      );
    }

    try {
      const snap = await getDocs(qHist);
      console.log('[FS][getDocs] CashRegisterCloseView.history', snap.size);
      if (cancelled) return;

      let docs = snap.docs;
      const hasNextPage = docs.length > PAGE_SIZE;
      setHasNext(hasNextPage);
      if (docs.length > PAGE_SIZE) docs = docs.slice(0, PAGE_SIZE);

      if (docs.length > 0) {
        const lastDoc = docs[docs.length - 1];
        setPageCursors((prev) => {
          const copy = prev.slice(0, page);
          copy[page] = lastDoc;
          return copy;
        });
      }

      const rows = docs
        .filter((d) => d.data()?.deleted !== true)
        .map((d) => {
          const data = d.data();
          const openedAt = data.openedAt?.toDate?.() || null;
          const closedAt = data.closedAt?.toDate?.() || null;
          return {
            id: d.id,
            openDate: openedAt ? openedAt.toLocaleDateString("es-CO") : "",
            openTime: openedAt ? openedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : "",
            closeDate: closedAt ? closedAt.toLocaleDateString("es-CO") : "",
            closeTime: closedAt ? closedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : "",
            user: data.user || "",
            amount: Number(data.montoApertura ?? data.amount ?? 0),
            finalAmount: Number(data.finalAmount ?? 0),
            difference: Number(data.difference ?? 0),
            status: data.status || data.estado || "",
            observations: data.observations || "",
            _closedAt: closedAt,
          };
        })
        .filter((r) => {
          const t = r._closedAt?.getTime?.() ?? null;
          if (!t) return false;
          if (from && t < from.getTime()) return false;
          if (to && t > to.getTime()) return false;
          return true;
        });

      setUserClosingHistory(rows);

      if (page === 0) {
        writeHistCache(cacheKey, { rows, hasNext: hasNextPage });
      }
    } catch (err) {
      console.error("No se pudo cargar historial de cierres", err);
      if (!cancelled) {
        setUserClosingHistory([]);
        setHasNext(false);
      }
    } finally {
      if (!cancelled) setLoadingHist(false);
    }
  };

  loadHist();
  return () => { cancelled = true; };
}, [currentUser, isStaff, histUser, histDateFrom, histDateTo, page, currentSubView]);

useEffect(() => {
  setPage(0);
  setPageCursors([]);
}, [histUser, histDateFrom, histDateTo, isStaff]);


// Cargar opciones de usuario para filtro (solo Admin/TI)
useEffect(() => {
  const self = currentUser?.email ?? currentUser ?? '';
  if (!isStaff) {
    setUserOptions(self ? [self] : []);
    return;
  }
  if (currentSubView !== 'history') return;
  const loadUsers = async () => {
    const qUsers = query(
      collection(db, "cajas"),
      where("estado", "==", "cerrada"),
      orderBy("openedAt", "desc"),
      limit(100)
    );
    try {
      const snap = await getDocs(qUsers);
      console.log('[FS][getDocs] CashRegisterCloseView.usersOptions', snap.size);
      const s = new Set();
      snap.docs.forEach(d => {
        const data = d.data() || {};
        if (data.deleted === true) return;
        const u = data.user;
        if (u) s.add(u);
      });
      setUserOptions(Array.from(s));
    } catch (err) {
      console.error("No se pudo cargar usuarios de cierres", err);
      setUserOptions(self ? [self] : []);
    }
  };
  loadUsers();
}, [currentUser, isStaff, currentSubView]);




// === CÁLCULOS DEL TURNO (con 2 métodos por venta y gastos) ===

// La venta se contabiliza por su valor original y cada reembolso se descuenta
// del método por el cual realmente salió el dinero.
const {
  salesByMethod,
  collectionsByMethod,
  efectivoRecibido,
  cambioEntregado,
  cashFromSalesNet,
  returnedTotal: totalSalesCancelledOrReturned,
} = calculateTurnSalesAccounting(salesInTurn);

// 3) KPIs por método
const TRANSF_METHODS = ['Nequi','Daviplata','Bancolombia','BBVA','Davivienda','Rappi','PSE','QR'];

// KPIs “brutos”
const totalCashSales = Number(salesByMethod['Efectivo']?.amount || 0);
const totalCardSales = Number(salesByMethod['Tarjeta']?.amount || 0);

// Suma todas las billeteras/bancos normalizados
const totalTransferSales = TRANSF_METHODS.reduce(
  (sum, m) => sum + Number(salesByMethod[m]?.amount || 0), 0
);

// “Otros” = todo lo que NO esté en las categorías anteriores
const totalOtherSales = Object.entries(salesByMethod)
  .filter(([k]) => !['Efectivo', 'Tarjeta', ...TRANSF_METHODS].includes(k))
  .reduce((s, [, v]) => s + Number(v.amount || 0), 0);

// 4) Ingresos/Salidas manuales (robusto con Number)
const totalManualIncoming = manualIncomingInTurn
  .filter(e => e.affectsCash !== false)
  .reduce((sum, e) => sum + Number(e.totalEntryCost || 0), 0);

// `salidas` representa consumo de inventario (productos/insumos) y no una
// salida de efectivo. Los egresos reales de caja se registran en `gastos`.
// Mantener este valor en cero alinea la vista con la conciliación autoritativa
// del backend y evita descontar dos veces el costo de inventario al cerrar.
const totalManualOutgoing = 0;

// 5) Gastos reales del turno
const totalExpensesInTurn = (expensesInTurn || []).reduce((s, e) => s + Number(e.amount || 0), 0);

// 6) Totales adicionales
const totalNumberOfSales = salesInTurn.length;
const totalProductsSold = salesInTurn.reduce(
  (sum, sale) => sum + (sale.products || []).reduce((s, p) => s + Number(p.quantity || 0), 0), 0
);
const baseAmount = Number(effectiveCash?.montoApertura ?? effectiveCash?.amount ?? 0);
const advanceTotal = advanceTransfers.reduce((sum, t) => sum + Number(t.amount || 0), 0);
const returnTotal = returnTransfers.reduce((sum, t) => sum + Number(t.amount || 0), 0);
const adjustedBaseAmount = baseAmount - advanceTotal + returnTotal;


// 8) Esperado en caja física = Base + Efectivo neto + Ingresos manuales − Salidas manuales − Gastos
const expectedTotalInCash =
  adjustedBaseAmount +
  cashFromSalesNet +
  totalManualIncoming -
  totalManualOutgoing -
  totalExpensesInTurn;

const difference = Number(closingAmount || 0) - expectedTotalInCash;

const handleCloseTurn = () => {
  // En vez de window.confirm, abrimos el modal
  abrirConfirmacionCierre();
};

const openTurnDetail = async (turnId) => {
  try {
    setDetailLoading(true);
    const ref = doc(db, "cajas", turnId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setDetailData({ id: snap.id, ...snap.data() });
      setDetailOpen(true);
    } else {
toast.info("No se encontró el detalle de este cierre.", { icon: "ℹ️", containerId: CLOSE_CASH_TOAST_ID });
    }
  } catch (e) {
    console.error(e);
toast.error("No se pudo cargar el detalle del cierre.", { icon: "⚠️", containerId: CLOSE_CASH_TOAST_ID });
  } finally {
    setDetailLoading(false);
  }
};

const openEditTurn = async (turnId) => {
  try {
    setEditLoading(true);
    const ref = doc(db, "cajas", turnId);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
toast.info("No se encontró este cierre.", { icon: "ℹ️", containerId: CLOSE_CASH_TOAST_ID });
      return;
    }
    const data = { id: snap.id, ...snap.data() };
    setEditData(data);
    setEditFinalAmount(String(Number(data.finalAmount ?? 0)));
    setEditObservations(data.observations || '');
    setEditOpen(true);
  } catch (e) {
    console.error(e);
toast.error("No se pudo cargar el cierre para edición.", { icon: "⚠️", containerId: CLOSE_CASH_TOAST_ID });
  } finally {
    setEditLoading(false);
  }
};

const closeEditTurn = () => {
  setEditOpen(false);
  setEditData(null);
  setEditFinalAmount('');
  setEditObservations('');
};
const saveEditTurn = async () => {
  if (!editData) return;

  const fa = Number(editFinalAmount || 0);
  const reason = String(editObservations || '').trim();
  if (!Number.isFinite(fa) || fa < 0) {
    toast.info('Ingresa un monto contado válido.', { containerId: CLOSE_CASH_TOAST_ID });
    return;
  }
  if (!reason) {
    toast.info('Escribe el motivo de la corrección en observaciones.', { containerId: CLOSE_CASH_TOAST_ID });
    return;
  }

  try {
    setEditLoading(true);
    const updated = await editCashClose(editData.id, {
      finalAmount: fa,
      observations: reason,
      reason,
    });
    if (!updated) throw new Error('El servidor no confirmó la corrección.');
    setUserClosingHistory((current) => current.map((item) => (
      item.id === editData.id
        ? {
            ...item,
            finalAmount: Number(updated.finalAmount),
            difference: Number(updated.difference),
            status: updated.status,
            observations: updated.observations,
          }
        : item
    )));
    if (detailData?.id === editData.id) {
      setDetailData((current) => current ? { ...current, ...updated } : current);
    }
    clearClosingHistoryCache();
    closeEditTurn();
toast.success("Cierre actualizado y diferencia recalculada.", { icon: "✅", containerId: CLOSE_CASH_TOAST_ID });
  } catch (e) {
    console.error(e);
 toast.error(e?.message || "No se pudo actualizar el cierre.", { icon: "⚠️", containerId: CLOSE_CASH_TOAST_ID });
  } finally {
    setEditLoading(false);
  }
};

const clearClosingHistoryCache = () => {
  try {
    const keys = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith('cashCloseHist:')) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (_error) {}
};

const handleDeleteClose = async (turn) => {
  if (!canDeleteClosings || !turn?.id || deletingCloseId) return;
  const confirmation = window.prompt(
    `Esta acción quitará el cierre de ${turn.user || 'este usuario'} del historial normal. Escribe ELIMINAR para continuar:`
  );
  if (confirmation === null) return;
  if (String(confirmation).trim().toUpperCase() !== 'ELIMINAR') {
    toast.info('No se eliminó el cierre: la confirmación no coincide.', { containerId: CLOSE_CASH_TOAST_ID });
    return;
  }
  const reason = window.prompt('Motivo de la eliminación del cierre:');
  if (reason === null) return;
  if (!String(reason).trim()) {
    toast.info('Debes escribir un motivo para eliminar el cierre.', { containerId: CLOSE_CASH_TOAST_ID });
    return;
  }

  setDeletingCloseId(turn.id);
  try {
    await deleteCashClose(turn.id, String(reason).trim());
    setUserClosingHistory((current) => current.filter((item) => item.id !== turn.id));
    clearClosingHistoryCache();
    if (detailData?.id === turn.id) closeTurnDetail();
    if (editData?.id === turn.id) closeEditTurn();
    toast.success('Cierre eliminado del historial. El registro de auditoría fue conservado.', {
      icon: '✅', containerId: CLOSE_CASH_TOAST_ID,
    });
  } catch (error) {
    console.error(error);
    toast.error(error?.message || 'No se pudo eliminar el cierre.', {
      icon: '⚠️', containerId: CLOSE_CASH_TOAST_ID,
    });
  } finally {
    setDeletingCloseId(null);
  }
};

const handleVoidReturn = async (transfer) => {
  if (!canDeleteClosings || !detailData?.id || !transfer?.id || voidingReturnId) return;
  const confirmed = window.confirm(
    `Se anulara la devolucion de ${fmtCOP(transfer.amount)} asociada a ${transfer.relatedUser || 'este usuario'} y se recalculara el cierre. Continuar?`
  );
  if (!confirmed) return;
  const reason = window.prompt('Motivo de la correccion:');
  if (reason === null) return;
  if (!String(reason).trim()) {
    toast.info('Debes escribir un motivo para corregir el cierre.', { containerId: CLOSE_CASH_TOAST_ID });
    return;
  }

  setVoidingReturnId(transfer.id);
  try {
    await voidCashReturn(detailData.id, transfer.id, String(reason).trim());
    const refreshed = await getDoc(doc(db, 'cajas', detailData.id));
    if (refreshed.exists()) setDetailData({ id: refreshed.id, ...refreshed.data() });
    clearClosingHistoryCache();
    toast.success('Devolucion anulada y cierre recalculado.', {
      icon: 'âœ…', containerId: CLOSE_CASH_TOAST_ID,
    });
  } catch (error) {
    console.error(error);
    toast.error(error?.message || 'No se pudo corregir la devolucion.', {
      icon: 'âš ï¸', containerId: CLOSE_CASH_TOAST_ID,
    });
  } finally {
    setVoidingReturnId(null);
  }
};

const closeTurnDetail = () => {
  setDetailOpen(false);
  setDetailData(null);
};

// Etiqueta legible para gastos (prioriza descripción y mapea tipos conocidos)
const labelForExpense = (g = {}) => {
  try {
    const desc = (g.description || '').toString().trim();
    if (desc) return desc;
    const t = (g.type || '').toString().toLowerCase();
    if (t === 'compra_inventario' || t === 'entrada_compra' || t === 'compra') {
      const supplier = (g.supplier || '').toString().trim();
      return supplier ? `Compra de inventario — ${supplier}` : 'Compra de inventario';
    }
    return (g.category || g.name || 'Gasto');
  } catch (_) {
    return (g.category || 'Gasto');
  }
};

// === Imprimir Informe de Cierre ===
const printCloseReport = () => {
 if (!effectiveCash) return;
  const isThermal = reportFormat === 'thermal';
  const turnStart = getTurnStart(effectiveCash);

const fechaApertura = turnStart ? turnStart.toLocaleDateString('es-CO') : '—';
  const horaApertura  = turnStart ? turnStart.toLocaleTimeString('es-CO', {hour:'2-digit', minute:'2-digit'}) : '—';

  const tr = (a,b) => `
    <tr>
      <td style="padding:4px 8px;border-bottom:1px solid #eee">${a}</td>
      <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${b}</td>
    </tr>`;

  const ventasMetRows = Object.entries(salesByMethod || {})
    .sort(([a],[b]) => a.localeCompare(b))
    .map(([k,v]) => tr(`${k}${v?.count ? ` (${v.count})` : ''}`, fmtCOP(Number(v?.amount||0))))
    .join('');

  const cobrosRows = Object.entries(collectionsByMethod || {})
    .sort(([a],[b]) => a.localeCompare(b))
    .map(([k,v]) => tr(`${k}${v?.count ? ` (${v.count})` : ''}`, fmtCOP(Number(v?.amount||0))))
    .join('');

  const gastosRows = (expensesInTurn || [])
    .map(g => tr(g.category || 'Gasto', fmtCOP(Number(g.amount || 0))))
    .join('') || tr('—', fmtCOP(0));

  const advanceTotalPrint = advanceTransfers.reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const returnTotalPrint = returnTransfers.reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const adjustedBasePrint = baseAmount - advanceTotalPrint + returnTotalPrint;
  const advanceRows =
    (advanceTransfers || [])
      .map((t) => tr(t.relatedUser || t.toTurnId || 'Sin usuario', fmtCOP(Number(t.amount || 0))))
      .join('') || tr('Sin movimientos', fmtCOP(0));
  const returnRows =
    (returnTransfers || [])
      .map((t) => tr(t.relatedUser || t.fromTurnId || 'Sin usuario', fmtCOP(Number(t.amount || 0))))
      .join('') || tr('Sin movimientos', fmtCOP(0));
  const html = `
  <!doctype html><html lang="es">
    <head>
      <meta charset="utf-8" />
      <title>Informe de Cierre</title>
      <style>
        @page { size: A4; margin: 18mm; }
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; }
        h1 { font-size: 20px; margin: 0 0 8px; }
        h2 { font-size: 16px; margin: 20px 0 8px; }
        .muted { color:#666; }
        .kpis { display:grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap:8px; }
        .card { background:#fafafa; border:1px solid #eee; border-radius:8px; padding:8px 12px; }
        table { width:100%; border-collapse: collapse; font-size: 13px; }
        .tot { font-weight:700; }
        .right { text-align:right; }
        .small { font-size:12px; }
        ${isThermal ? `
        @page { size: auto; margin: 0; }
        * { box-sizing: border-box; }
        html { width:80mm; margin:0; padding:0; }
        body { width:80mm; margin:0; padding:4mm; font-family:Arial, Helvetica, sans-serif; font-size:16px; line-height:1.3; color:#000; overflow-wrap:anywhere; }
        h1 { text-align:center; font-size:20px; margin:0 0 8px; }
        h2 { font-size:17px; margin:14px 0 6px; padding-top:8px; border-top:1px dashed #000; break-after:avoid; }
        h3 { font-size:16px; margin:10px 0 4px; break-after:avoid; }
        .muted { color:#000; }
        .small { font-size:14px; }
        .kpis { display:block; }
        .card { background:#fff; border:0; border-bottom:1px dotted #000; border-radius:0; padding:5px 0; break-inside:avoid; }
        table { font-size:16px; table-layout:fixed; }
        td { padding:5px 0 !important; border-bottom:1px dotted #000 !important; vertical-align:top; overflow-wrap:anywhere; }
        td:first-child { width:56%; padding-right:6px !important; }
        td:last-child { width:44%; text-align:right; font-weight:700; }
        tr { break-inside:avoid; }
        .tot { font-size:18px; }
        ` : ''}
      </style>
    </head>
    <body>
      <h1>Informe de Cierre de Turno</h1>
      <div class="small muted">Generado: ${new Date().toLocaleString('es-CO')}</div>

      <h2>Datos del Turno</h2>
      <div class="kpis">
<div class="card"><div class="muted small">Usuario</div><div><b>${effectiveCash?.user || (currentUser?.email ?? '—')}</b></div></div>
 <div class="card"><div class="muted small">Estado</div><div><b>${effectiveCash?.estado || effectiveCash?.status || 'abierta'}</b></div></div>
        <div class="card"><div class="muted small">Fecha Apertura</div><div><b>${fechaApertura}</b></div></div>
        <div class="card"><div class="muted small">Hora Apertura</div><div><b>${horaApertura}</b></div></div>
        <div class="card"><div class="muted small">Monto Base</div><div><b>${fmtCOP(baseAmount)}</b></div></div>
        <div class="card"><div class="muted small">Base ajustada</div><div><b>${fmtCOP(adjustedBasePrint)}</b></div></div>
        <div class="card"><div class="muted small">Avances a meseros</div><div><b>${fmtCOP(advanceTotalPrint)}</b></div></div>
        <div class="card"><div class="muted small">Dinero recuperado</div><div><b>${fmtCOP(returnTotalPrint)}</b></div></div>
        <div class="card"><div class="muted small"># Ventas</div><div><b>${totalNumberOfSales}</b></div></div>
      </div>

      <h2>Ventas por método (netas)</h2>
      <table>${ventasMetRows || tr('—', fmtCOP(0))}</table>

      <div class="kpis" style="margin-top:8px">
        <div class="card"><div class="muted small">Ventas en Efectivo</div><div><b>${fmtCOP(totalCashSales)}</b></div></div>
        <div class="card"><div class="muted small">Ventas con Tarjeta</div><div><b>${fmtCOP(totalCardSales)}</b></div></div>
        <div class="card"><div class="muted small">Ventas por Transferencia</div><div><b>${fmtCOP(totalTransferSales)}</b></div></div>
        <div class="card"><div class="muted small">Otros Ingresos (man.)</div><div><b>${fmtCOP(totalManualIncoming)}</b></div></div>
      </div>

      <h2>Cobros por método (recibido)</h2>
      <table>${cobrosRows || tr('—', fmtCOP(0))}</table>
      <div class="small muted" style="margin-top:6px">
        Efectivo recibido: <b>${fmtCOP(efectivoRecibido)}</b> ·
        Cambio entregado: <b>${fmtCOP(cambioEntregado)}</b> ·
        Efectivo neto de ventas: <b>${fmtCOP(cashFromSalesNet)}</b>
      </div>

      <h2>Gastos Registrados en el Turno</h2>
      <table>
        ${gastosRows}
        <tr class="tot"><td style="padding:6px 8px;">Total Gastos</td><td class="right" style="padding:6px 8px;">${fmtCOP(totalExpensesInTurn)}</td></tr>
      </table>

      <h2>Balance Final del Turno</h2>
      <table>
        ${tr('Total Esperado en Caja', fmtCOP(expectedTotalInCash))}
        ${tr('Monto Contado en Caja', fmtCOP(Number(closingAmount || 0)))}
        ${tr('Diferencia', fmtCOP(difference))}
      </table>

      <h2>Movimientos Internos</h2>
      <h3>Base asignada a meseros</h3>
      <table>${advanceRows}</table>
      <h3>Dinero recuperado del mesero</h3>
      <table>${returnRows}</table>

      <h2>Observaciones</h2>
      <div class="card" style="min-height:48px">${(observations || '—')
        .toString()
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>

      <script>
        window.onload = () => {
          if (${isThermal}) {
            // Dimensionar el rollo con el contenido real, sin reducir una hoja A4.
            const heightMm = Math.ceil(document.body.getBoundingClientRect().height * 25.4 / 96) + 2;
            const pageStyle = document.createElement('style');
            pageStyle.textContent = '@page { size: 80mm ' + heightMm + 'mm; margin: 0; }';
            document.head.appendChild(pageStyle);
          }
          window.onafterprint = () => window.close();
          window.print();
        };
      </script>
    </body>
  </html>`;

  const w = window.open('', '_blank');
  if (!w) return alert('El navegador bloqueó la ventana de impresión.');
  w.document.open();
  w.document.write(html);
  w.document.close();
};


const renderSubView = () => {
  switch (currentSubView) {
case 'close': {
  if (!effectiveCash) {
   return (
     <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto mb-8 text-center">
       {loadingOpenTurn ? 'Cargando datos de la caja...' : 'No tienes un turno abierto.'}
     </div>
    );
  }
  const turnStart = getTurnStart(effectiveCash);

return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-5xl mx-auto mb-8 space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Detalles del turno actual</h2>
          <p className="text-sm text-gray-500">
            Usuario: <span className="font-semibold text-gray-800">{effectiveCash?.user ?? (currentUser?.email ?? '—')}</span>
          </p>
          <p className="text-sm text-gray-500">
            Estado: <span className="font-semibold text-emerald-600">{effectiveCash?.estado || effectiveCash?.status || 'abierta'}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCloseTurn}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 shadow"
          >
            Cerrar turno
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Formato
            <select
              value={reportFormat}
              onChange={(event) => setReportFormat(event.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-2 py-2"
            >
              <option value="thermal">Térmica 80 mm (SAT)</option>
              <option value="a4">Hoja A4 / PDF</option>
            </select>
          </label>
          <button
            onClick={printCloseReport}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 shadow"
          >
            Imprimir informe
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Base inicial', value: fmtCOP(baseAmount) },
            { label: 'Abonos (man. + avances)', value: fmtCOP(totalManualIncoming + advanceTotal) },
            { label: 'Retiros del mesero', value: fmtCOP(returnTotal) },
            { label: 'Caja esperada', value: fmtCOP(expectedTotalInCash) },
          ].map((card) => (
          <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-4 text-sm flex flex-col">
            <span className="text-xs uppercase tracking-wide text-gray-500">{card.label}</span>
            <span className="text-lg font-semibold text-gray-900">{card.value}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
          {advanceTransfers.length > 0 && (
            <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-lg font-semibold text-emerald-800">Base asignada a meseros</div>
              <div className="mt-3 space-y-2 text-sm text-gray-800">
                {advanceTransfers.map((t) => (
                  <div key={`adv-${t.id}`} className="flex justify-between">
                    <span>{t.relatedUser || t.toTurnId}</span>
                    <span className="font-semibold">{fmtCOP(t.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {returnTransfers.length > 0 && (
            <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="text-lg font-semibold text-blue-800">Dinero recuperado del mesero</div>
              <div className="mt-3 space-y-2 text-sm text-gray-800">
                {returnTransfers.map((t) => (
                  <div key={`ret-${t.id}`} className="flex justify-between">
                    <span>{t.relatedUser || t.fromTurnId}</span>
                    <span className="font-semibold">{fmtCOP(t.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

      </div>

<section className="space-y-6">
  <h3 className="text-2xl font-bold text-gray-800 border-t pt-6">Totales del movimiento en el turno</h3>
  <div className="grid gap-3 md:grid-cols-2">
    {[
      { label: 'Ventas en efectivo', value: fmtCOP(totalCashSales) },
      { label: 'Ventas con tarjeta', value: fmtCOP(totalCardSales) },
      { label: 'Ventas por transferencia', value: fmtCOP(totalTransferSales) },
      { label: 'Otros ingresos (manuales)', value: fmtCOP(totalManualIncoming) },
      { label: 'Salidas manuales', value: fmtCOP(totalManualOutgoing) },
      { label: 'Ventas anuladas/devueltas', value: fmtCOP(totalSalesCancelledOrReturned) },
      ].concat([
        { label: '# Ventas', value: totalNumberOfSales },
        { label: 'Productos vendidos', value: totalProductsSold },
      ]).map(stat => (
      <div key={stat.label} className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex justify-between text-sm font-medium">
        <span className="text-gray-600">{stat.label}</span>
        <span className="text-gray-900">{stat.value}</span>
      </div>
    ))}
  </div>

  <div className="space-y-4">
    <h4 className="text-lg font-semibold text-gray-800">Ventas por método (netas)</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {['Efectivo', 'Tarjeta'].map((method) => (
        salesByMethod[method]?.amount > 0 && (
          <div key={method} className="flex justify-between text-sm">
            <span>{method}{salesByMethod[method]?.count ? ` (${salesByMethod[method].count})` : ''}</span>
            <span>{fmtCOP(salesByMethod[method].amount)}</span>
          </div>
        )
      ))}
      {Object.entries(salesByMethod)
        .filter(([k, v]) => !['Efectivo','Tarjeta'].includes(k) && Number(v?.amount) > 0)
        .sort(([a],[b]) => a.localeCompare(b))
        .map(([method, info]) => (
          <div key={method} className="flex justify-between text-sm">
            <span>{method}{info.count ? ` (${info.count})` : ''}</span>
            <span>{fmtCOP(info.amount)}</span>
          </div>
        ))}
    </div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm flex justify-between">
      <span>Efectivo recibido</span>
      <span>{fmtCOP(efectivoRecibido)}</span>
    </div>
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm flex justify-between">
      <span>Cambio entregado</span>
      <span>{fmtCOP(cambioEntregado)}</span>
    </div>
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm flex justify-between">
      <span>Efectivo neto de ventas</span>
      <span>{fmtCOP(cashFromSalesNet)}</span>
    </div>
  </div>

  <details className="rounded-xl border border-dashed border-gray-300 bg-slate-50 p-4 text-sm text-gray-600">
    <summary className="cursor-pointer font-semibold">Ver detalle: Ventas asignadas por método (opcional)</summary>
    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
      {Object.entries(salesByMethod).map(([method, info]) => (
        <div key={method} className="flex justify-between">
          <span>{method}{info.count ? ` (${info.count})` : ''}</span>
          <span>{fmtCOP(info.amount)}</span>
        </div>
      ))}
    </div>
  </details>
</section>


        <section className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-800 border-t pt-6">Gastos registrados en el turno</h3>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
            {expensesInTurn.length > 0 ? (
              <>
                {expensesInTurn.map(exp => (
                  <div key={exp.id} className="flex justify-between text-sm font-medium text-gray-700">
                    <span>{labelForExpense(exp)}</span>
                    <span>{fmtCOP(exp.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold text-gray-900 border-t pt-2 mt-2">
                  <span>Total gastos</span>
                  <span>{fmtCOP(totalExpensesInTurn)}</span>
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500">Sin gastos registrados en este turno.</p>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-800 border-t pt-6">Balance final del turno</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
              <p className="text-sm text-gray-500">Total esperado en caja</p>
              <p className="text-2xl font-semibold text-blue-600">{fmtCOP(expectedTotalInCash)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
              <p className="text-sm text-gray-500">Monto contado en caja</p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={closingAmountInput}
                onChange={(e) => {
                  let v = e.target.value.replace(/\D/g, '');
                  v = v.replace(/^0+/, '') || '0';
                  setClosingAmountInput(v);
                }}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <p className={`text-lg font-bold ${difference === 0 ? 'text-green-600' : difference > 0 ? 'text-blue-600' : 'text-red-600'}`}>
            Diferencia: {fmtCOP(difference)} {difference === 0 ? '(OK)' : difference > 0 ? '(Sobrante)' : '(Faltante)'}
          </p>
        </section>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones finales</label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 resize-none"
            />
          </div>
        </div>
 );
    }
    case 'history':
              return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historial de Cierres de Caja</h2>
            {/* Filtros */}
<div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
  <div>
    <label className="block text-xs text-gray-600 mb-1">Fecha desde</label>
    <input
      type="date"
      value={histDateFrom}
      onChange={(e) => setHistDateFrom(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
  <div>
    <label className="block text-xs text-gray-600 mb-1">Fecha hasta</label>
    <input
      type="date"
      value={histDateTo}
      onChange={(e) => setHistDateTo(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
  <div className="md:col-span-2">
    <label className="block text-xs text-gray-600 mb-1">Usuario</label>
    <select
      value={histUser}
      onChange={(e) => setHistUser(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg"
      disabled={!isStaff && userOptions.length <= 1}
    >
      <option value="">Todos</option>
      {userOptions.map(u => (
        <option key={u} value={u}>{u}</option>
      ))}
    </select>
  </div>
</div>

            
            <div className="overflow-x-auto">
<table className="min-w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Inicial</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto contado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Diferencia</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observaciones</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalle</th>

                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userClosingHistory.length > 0 ? (
                  userClosingHistory.map((turn) => (
  <tr key={turn.id} className="hover:bg-gray-50 transition duration-150">
<td className="px-6 py-4 text-sm text-gray-500">
  <div className="leading-tight">
    <div><span className="text-xs text-gray-400">Apertura:</span> {turn.openDate} {turn.openTime}</div>
   <div><span className="text-xs text-gray-400">Cierre:</span> {turn.closeDate} {turn.closeTime}</div>
  </div>
 </td>
 <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-[240px] truncate" title={turn.user}>
  {turn.user}
 </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmtCOP(turn.amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmtCOP(turn.finalAmount)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${turn.difference === 0 ? 'text-green-600' : (turn.difference > 0 ? 'text-blue-600' : 'text-red-600')}`}>
{fmtCOP(turn.difference)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{turn.status}</td>
<td className="px-6 py-4 text-sm text-gray-500 max-w-[320px] truncate" title={turn.observations || 'N/A'}>
  {turn.observations || 'N/A'}
 </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
  <button
    onClick={() => openTurnDetail(turn.id)}
    className="px-3 py-1 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
    title="Ver detalle del cierre"
  >
    Ver detalle
  </button>
 {canEditClosings && String(turn.status || '').toLowerCase().includes('cerrad') && (
   <button
   onClick={() => openEditTurn(turn.id)}
   className="ml-2 px-3 py-1 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700"
   title="Editar cierre"
  >
   Editar
  </button>
  )}
  {canDeleteClosings && (
    <button
      type="button"
      disabled={deletingCloseId === turn.id}
      onClick={() => handleDeleteClose(turn)}
      className="ml-2 px-3 py-1 rounded-md bg-red-700 text-white text-sm hover:bg-red-800 disabled:cursor-wait disabled:opacity-60"
      title="Eliminar cierre (acceso restringido)"
    >
      {deletingCloseId === turn.id ? 'Eliminando…' : 'Eliminar'}
    </button>
  )}</td>

                      </tr>
                    ))
                  ) : (
                    <tr>
<td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay historial de cierres para este usuario.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

<div className="flex items-center justify-between mt-4">
  <div className="text-sm text-gray-500">
    {loadingHist ? 'Cargando…' : `Mostrando ${userClosingHistory.length} de ${PAGE_SIZE}${hasNext ? '+' : ''}`}
  </div>
  <div className="flex gap-2">
    <button
      onClick={() => setPage((p) => Math.max(0, p - 1))}
      disabled={page === 0 || loadingHist}
      className="px-3 py-1 rounded-md border disabled:opacity-50"
    >
      ← Anterior
    </button>
    <span className="px-2 py-1 text-sm text-gray-600">Página {page + 1}</span>
    <button
      onClick={() => setPage((p) => p + 1)}
      disabled={!hasNext || loadingHist}
      className="px-3 py-1 rounded-md border disabled:opacity-50"
    >
      Siguiente →
    </button>
  </div>
</div>


            </div>
          </div>
        );
      default:
        return null;
    }
  };


  return (
    <>
      <ToastContainer
        containerId={CLOSE_CASH_TOAST_ID}
        position="top-center"
        autoClose={2200}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Cierre de Turno de Caja</h1>
        <div className="w-32"></div>
      </div>

      <div className="mb-6 flex justify-center space-x-4">
        <button
          onClick={() => setCurrentSubView('close')}
          className={`px-6 py-2 rounded-lg font-semibold transition duration-200 ${currentSubView === 'close' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
        >
          Cerrar Turno Actual
        </button>
        <button
          onClick={() => setCurrentSubView('history')}
          className={`px-6 py-2 rounded-lg font-semibold transition duration-200 ${currentSubView === 'history' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
        >
          Historial de Cierres
        </button>
      </div>

      {renderSubView()}
{detailOpen && (
  <div className="fixed inset-0 z-50">
    {/* Backdrop */}
    <div className="absolute inset-0 bg-black/50" onClick={closeTurnDetail} />

    {/* Wrapper que permite desplazamiento si el panel es alto */}
    <div className="relative h-full w-full overflow-y-auto">
      {/* Espaciado superior/inferior para que respire */}
      <div className="min-h-full flex items-start justify-center p-4 md:p-6 lg:p-8">

        {/* Panel */}
        <div
          className="
            relative w-full 
            max-w-[56rem]   /* ~896px: más contenido que un modal chico, pero sin ser enorme */
            md:max-w-3xl    /* 768px en md */
            bg-white rounded-none md:rounded-2xl shadow-2xl
            p-4 md:p-6
            max-h-[88vh] overflow-y-auto /* <- clave: el contenido scrollea dentro del panel */
          "
        >
          {/* X cerrar */}
          <button
            onClick={closeTurnDetail}
            className="absolute right-3 top-3 p-2 rounded-md hover:bg-gray-100"
            title="Cerrar"
          >
            ✕
          </button>

          {/* Header */}
          <h3 className="text-lg md:text-2xl font-bold text-gray-900 pr-10">
            Detalle del cierre
          </h3>

          {detailLoading && (
            <div className="mt-4 text-gray-600">Cargando detalle…</div>
          )}

          {!detailLoading && detailData && (
            <div className="space-y-4 md:space-y-6 mt-4">
              {/* Encabezado compacto en móvil */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <p className="text-sm md:text-base"><strong>Usuario:</strong> {detailData.user || '—'}</p>
                <p className="text-sm md:text-base"><strong>Cerrado por:</strong> {detailData.closedBy || detailData.user || '—'}</p>
                <p className="text-sm md:text-base"><strong>Fecha apertura:</strong> {detailData.fecha || detailData.openedAt?.toDate?.().toLocaleDateString('es-CO') || '—'}</p>
                <p className="text-sm md:text-base"><strong>Hora apertura:</strong> {detailData.hora || '—'}</p>
                <p className="text-sm md:text-base"><strong>Hora cierre:</strong> {detailData.closingTime || detailData.closedAt?.toDate?.().toLocaleTimeString('es-CO') || '—'}</p>
                <p className="text-sm md:text-base"><strong>Estado:</strong> {detailData.status || detailData.estado || '—'}</p>
              </div>

              {/* Totales base */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <div className="p-3 md:p-4 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500">Monto Inicial</p>
                  <p className="text-base md:text-lg font-bold">{fmtCOP(Number(detailData.montoApertura ?? detailData.amount ?? 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500">Esperado en Caja</p>
                  <p className="text-base md:text-lg font-bold">{fmtCOP(Number(detailData.expectedTotalInCash || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500">Monto Contado</p>
                  <p className="text-base md:text-lg font-bold">{fmtCOP(Number(detailData.finalAmount || 0))}</p>
                </div>
              </div>

              {/* Diferencia */}
              <div className="p-3 md:p-4 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-500">Diferencia</p>
                <p className={`text-lg md:text-xl font-bold ${Number(detailData.difference || 0) === 0 ? 'text-green-600' : (Number(detailData.difference || 0) > 0 ? 'text-blue-600' : 'text-red-600')}`}>
                  {fmtCOP(Number(detailData.difference || 0))} {Number(detailData.difference || 0) === 0 ? '(OK)' : (Number(detailData.difference || 0) > 0 ? '(Sobrante)' : '(Faltante)')}
                </p>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Ventas en Efectivo</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalCashSales || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Ventas con Tarjeta</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalCardSales || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Ventas por Transferencia</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalTransferSales || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Otros Ingresos (man.)</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalManualIncoming || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Salidas (man.)</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalManualOutgoing || 0))}</p>
                </div>
                <div className="p-3 md:p-4 rounded-lg bg-white border">
                  <p className="text-xs text-gray-500">Gastos</p>
                  <p className="text-sm md:text-base font-semibold">{fmtCOP(Number(detailData.totalExpensesInTurn || 0))}</p>
                </div>
              </div>

              {/* Desglose por métodos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <h4 className="font-bold mb-2 text-sm md:text-base">Ventas por método (netas)</h4>
                  <div className="rounded-lg border divide-y">
                    {Object.entries(detailData?.paymentBreakdown?.ventas || {}).length === 0 && (
                      <p className="p-3 text-sm text-gray-500">Sin datos</p>
                    )}
                    {Object.entries(detailData?.paymentBreakdown?.ventas || {}).map(([m, info]) => (
                      <div key={m} className="flex justify-between p-3 text-sm md:text-base">
                        <span>{m} {info?.count ? `(${info.count})` : ''}</span>
                        <span className="font-semibold">{fmtCOP(Number(info?.amount || 0))}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold mb-2 text-sm md:text-base">Cobros por método (recibido)</h4>
                  <div className="rounded-lg border divide-y">
                    {Object.entries(detailData?.paymentBreakdown?.cobros || {}).length === 0 && (
                      <p className="p-3 text-sm text-gray-500">Sin datos</p>
                    )}
                    {Object.entries(detailData?.paymentBreakdown?.cobros || {}).map(([m, info]) => (
                      <div key={m} className="flex justify-between p-3 text-sm md:text-base">
                        <span>{m} {info?.count ? `(${info.count})` : ''}</span>
                        <span className="font-semibold">{fmtCOP(Number(info?.amount || 0))}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs md:text-sm text-gray-700">
                    <p><strong>Efectivo recibido:</strong> {fmtCOP(Number(detailData?.efectivoRecibido || 0))}</p>
                    <p><strong>Cambio entregado:</strong> {fmtCOP(Number(detailData?.cambioEntregado || 0))}</p>
                    <p><strong>Efectivo neto de ventas:</strong> {fmtCOP(Number(detailData?.cashFromSalesNet || 0))}</p>
                  </div>
                </div>
              </div>

              {(detailData.advanceTransfersSnapshot?.length > 0 || detailData.returnTransfersSnapshot?.length > 0) && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {detailData.advanceTransfersSnapshot?.length > 0 && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-emerald-800">Avances a meseros</span>
                          <span className="text-xs text-emerald-600">Total {fmtCOP(detailData.advanceTotal || 0)}</span>
                        </div>
                        <div className="space-y-2 text-sm text-gray-800">
                          {detailData.advanceTransfersSnapshot.map((t) => (
                            <div
                              key={t.id}
                              className="flex items-center justify-between gap-2 rounded-md bg-white/60 px-3 py-2 border border-emerald-100"
                            >
                              <span className="truncate">{t.relatedUser}</span>
                              <span className="font-semibold">{fmtCOP(t.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {detailData.returnTransfersSnapshot?.length > 0 && (
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-blue-800">Dinero recuperado</span>
                          <span className="text-xs text-blue-600">Total {fmtCOP(detailData.returnTotal || 0)}</span>
                        </div>
                        <div className="space-y-2 text-sm text-gray-800">
                          {detailData.returnTransfersSnapshot.map((t) => (
                            <div
                              key={t.id}
                              className="flex items-center justify-between gap-2 rounded-md bg-white/60 px-3 py-2 border border-blue-100"
                            >
                              <div className="min-w-0">
                                <div className="truncate">{t.relatedUser}</div>
                                {t.description && <div className="truncate text-xs text-blue-700">{t.description}</div>}
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="font-semibold">{fmtCOP(t.amount)}</span>
                                {canDeleteClosings && (
                                  <button
                                    type="button"
                                    onClick={() => handleVoidReturn(t)}
                                    disabled={Boolean(voidingReturnId)}
                                    className="rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  >
                                    {voidingReturnId === t.id ? 'Corrigiendo...' : 'Anular'}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Gastos */}
              <div>
                <h4 className="font-bold mb-2 text-sm md:text-base">Gastos del turno</h4>
                <div className="rounded-lg border divide-y">
                  {(detailData?.expensesInTurn || []).length === 0 && (
                    <p className="p-3 text-sm text-gray-500">Sin gastos registrados</p>
                  )}
                  {(detailData?.expensesInTurn || []).map((g) => (
                    <div key={g.id || `${g.category}-${g.amount}-${g.date || ''}`} className="flex justify-between p-3 text-sm md:text-base">
                      <span>{g.category || 'Gasto'}</span>
                      <span className="font-semibold">{fmtCOP(Number(g.amount || 0))}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <h4 className="font-bold mb-2 text-sm md:text-base">Observaciones</h4>
                <p className="p-3 rounded-lg border bg-gray-50 text-sm whitespace-pre-wrap">
                  {detailData.observations || '—'}
                </p>
              </div>

              {/* Acciones */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 md:gap-3 pt-1">
                <button
                  onClick={() => window.print()}
                  className="w-full sm:w-auto px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Imprimir detalle
                </button>
                <button
                  onClick={closeTurnDetail}
                  className="w-full sm:w-auto px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}

{editOpen && (
  <div className="fixed inset-0 z-50">
    <div className="absolute inset-0 bg-black/50" onClick={closeEditTurn} />
    <div className="relative h-full w-full overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4 md:p-6 lg:p-8">
        <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl p-6">
          <button
            onClick={closeEditTurn}
            className="absolute right-3 top-3 p-2 rounded-md hover:bg-gray-100"
            title="Cerrar"
          >
            ✕
          </button>

          <h3 className="text-xl font-bold mb-4">Editar cierre</h3>
          {editLoading && <p className="text-gray-600">Cargando…</p>}

          {!editLoading && editData && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-2">
                <p className="text-sm text-gray-600">
                  <strong>Usuario:</strong> {editData.user || '—'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Esperado en caja:</strong> {fmtCOP(Number(editData.expectedTotalInCash || 0))}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto contado en caja (Final)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editFinalAmount}
                  onChange={(e) => {
                    let v = e.target.value.replace(/\D/g, '');
                    v = v.replace(/^0+/, '') || '0';
                    setEditFinalAmount(v);
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones / motivo de la corrección
                </label>
                <textarea
                  rows="3"
                  value={editObservations}
                  onChange={(e) => setEditObservations(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
              </div>

              {/* Preview de diferencia recalculada */}
              <div className="p-3 rounded-lg bg-gray-50">
                {(() => {
                  const fa = Number(editFinalAmount || 0);
                  const expected = Number(editData.expectedTotalInCash || 0);
                  const diff = fa - expected;
                  const label = diff === 0 ? '(OK)' : (diff > 0 ? '(Sobrante)' : '(Faltante)');
                  return (
                    <p className={`font-semibold ${diff === 0 ? 'text-green-600' : (diff > 0 ? 'text-blue-600' : 'text-red-600')}`}>
                      Nueva diferencia: {fmtCOP(diff)} {label}
                    </p>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={closeEditTurn}
                  className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveEditTurn}
                  className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                  disabled={editLoading}
                >
                  Guardar cambios
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}
  {confirmOpen && (
      <div className="fixed inset-0 z-50">
        {/* Fondo */}
        <div className="absolute inset-0 bg-black/50" onClick={cerrarConfirmacion} />

        {/* Contenedor scrollable */}
        <div className="relative h-full w-full overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-4 md:p-6">
            {/* Panel */}
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
              {/* Cerrar (X) */}
              <button
                onClick={cerrarConfirmacion}
                className="absolute right-3 top-3 p-2 rounded-md hover:bg-gray-100"
                title="Cerrar"
              >
                ✕
              </button>

              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-1">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {confirmOpen === "empty" ? "No hay ventas en este turno" : "Confirmar cierre de turno"}
                  </h3>
                  <p className="mt-2 text-gray-600">
                    {confirmOpen === "empty"
                      ? "No se han registrado ventas. ¿Deseas cerrar el turno de todos modos?"
                      : "¿Estás seguro de que quieres cerrar este turno de caja? Esta acción guardará los totales finales."}
                  </p>

                  <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm">
                    <div className="flex justify-between"><span>Esperado en caja</span><span className="font-semibold">{fmtCOP(expectedTotalInCash)}</span></div>
                    <div className="flex justify-between"><span>Contado (ingresado)</span><span className="font-semibold">{fmtCOP(Number(closingAmount || 0))}</span></div>
                    <div className={`flex justify-between ${difference === 0 ? 'text-green-600' : (difference > 0 ? 'text-blue-600' : 'text-red-600')}`}>
                      <span>Diferencia</span><span className="font-semibold">{fmtCOP(difference)} {difference === 0 ? '(OK)' : (difference > 0 ? '(Sobrante)' : '(Faltante)')}</span>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={cerrarConfirmacion} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800">
                      Cancelar
                    </button>
                    <button onClick={confirmarCierreTurno} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white">
                      Sí, cerrar turno
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    )}
      
      </div>
    </>
  );
};

export default CashRegisterCloseView;
